import { Component, computed, inject, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { I18nService } from '../../i18n/i18n.service';
import { MyAuthService } from '../../../auth/auth.service';
import { formatAppDate } from '../../date/app-date-format';
import { MonthLockService } from '../month-lock.service';
import { formatMonthLabel } from '../month-lock-format';

/**
 * "October 2026 of Lyon is unlocked for you until 14:30…" — shown app-wide
 * while the signed-in user is on an escape-hatch unlock (see
 * MonthLockService.myUnlock), with a button to relock it. Translated: the
 * people added to an unlock (`~unlockadd`) can be client accounts.
 *
 * Loads its own namespace against the root I18nService rather than via
 * `provideI18nScope`, so it never changes the scope of bindings a caller puts
 * on its host tag (see shared/i18n/README.md, multi-scope setup).
 */
@Component({
  selector: 'app-month-unlock-banner',
  imports: [MatIconModule],
  templateUrl: './month-unlock-banner.html',
  styleUrl: './month-unlock-banner.scss',
})
export class MonthUnlockBanner {
  private static readonly SCOPE = 'shared/month-lock';
  private readonly i18n = inject(I18nService);
  private readonly authService = inject(MyAuthService);
  private readonly monthLock = inject(MonthLockService);
  private readonly scopeReady: Signal<boolean>;

  readonly message = computed(() => {
    const unlock = this.monthLock.myUnlock();
    if (!unlock || !this.scopeReady()) return '';
    const parts = [
      this.t('month_unlocked_banner', {
        month: formatMonthLabel(unlock.month, this.i18n.lang()),
        plant: unlock.plantName ?? unlock.plantId,
        time: formatAppDate(unlock.expiresAt, 'time'),
        reason: unlock.reason,
      }),
    ];
    if (!unlock.isHolder && unlock.openedBy) {
      parts.push(this.t('month_unlocked_opened_by', { email: unlock.openedBy }));
    }
    const me = this.authService.ensuredUser()?.email?.toLowerCase();
    const others = unlock.delegates.filter((email) => email.toLowerCase() !== me);
    if (others.length) {
      parts.push(this.t('month_unlocked_also', { emails: others.join(', ') }));
    }
    return parts.join(' ');
  });

  readonly relockLabel = computed(() =>
    this.scopeReady() ? this.i18n.t('btn_relock_now', MonthUnlockBanner.SCOPE) : '',
  );

  constructor() {
    this.loadI18nScope();
    this.scopeReady = this.i18n.getScopeReady(MonthUnlockBanner.SCOPE);
  }

  protected relock(): void {
    this.monthLock.relock().subscribe();
  }

  private t(key: string, params: Record<string, string>): string {
    return this.i18n.t(key, MonthUnlockBanner.SCOPE, params);
  }

  /** Kept out of the constructor body: Sonar flags a Promise-returning call there. */
  private loadI18nScope(): void {
    void this.i18n.loadScope(MonthUnlockBanner.SCOPE);
  }
}
